"use client";

import cnBind from "classnames/bind";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type PointerEvent as ReactPointerEvent,
} from "react";

import { ModalCallBack } from "@/components/_Modals/ModalCallBack";
import {
    formatCampaignDate,
    isCampaignOfferActive,
    remainingPlacesOfMax,
    type PublicCampaign,
} from "@/shared/campaign/beautyCampaign";
import { useOnboardModal } from "@/shared/hooks/useOnboardModal";
import { trackBeautyCampaignEvent } from "@/shared/utils/beautyCampaignAnalytics";
import { getPresetBySlug } from "@/data/marketing/content";
import { startHref } from "@/components/marketing/launch";
import type { IndustryPresetKey } from "@/data/industryPresets";
import { INDUSTRY_PRESETS } from "@/data/industryPresets";

import { useTranslation } from "react-i18next";

import { useIndustryPreset } from "./IndustryPresetContext";

import styles from "./HeroSection.module.scss";

const cx = cnBind.bind(styles);

/** Image pairs only — captions come from i18n `home.hero.demos`. */
const PLATFORM_DEMO_ASSETS = [
    {
        phone: "/images/presets/hero-line-studio-miniapp.png",
        crm: "/images/presets/hero-line-studio-crm.png",
    },
    {
        phone: "/images/presets/ecom-home.png",
        crm: "/images/presets/ecom-crm-order-card.png",
    },
    {
        phone: "/images/presets/horeca-home.png",
        crm: "/images/presets/horeca-crm-hall.png",
    },
    {
        phone: "/images/presets/fitness-home.png",
        crm: "/images/presets/fitness-crm-services.png",
    },
    {
        phone: "/images/presets/service-home.png",
        crm: "/images/presets/service-crm-specialists.png",
    },
    {
        phone: "/images/presets/ecom-checkout.png",
        crm: "/images/presets/ecom-crm-products.png",
    },
    {
        phone: "/images/presets/beauty-home.png",
        crm: "/images/presets/beauty-crm-records.png",
    },
    {
        phone: "/images/presets/horeca-booking.png",
        crm: "/images/presets/horeca-crm-seating.png",
    },
] as const;

function useRotatingLine(lines: string[], enabled: boolean, reduceMotion: boolean | null) {
    const [index, setIndex] = useState(0);
    const key = lines.join("|");
    useEffect(() => {
        setIndex(0);
    }, [key]);
    useEffect(() => {
        if (!enabled || reduceMotion || lines.length <= 1) return undefined;
        const id = window.setInterval(() => setIndex((i) => (i + 1) % lines.length), 2800);
        return () => window.clearInterval(id);
    }, [enabled, key, lines.length, reduceMotion]);
    return lines[index] ?? lines[0] ?? "";
}

export const HeroSection = ({
    campaign,
    campaignLoading,
}: {
    campaign?: PublicCampaign | null;
    campaignLoading?: boolean;
}) => {
    const { t } = useTranslation("common");
    const reduceMotion = useReducedMotion();
    const { activePreset, preset, isPlatform } = useIndustryPreset();
    const marketingPreset = getPresetBySlug(activePreset);
    const { isModalOpen, leadIntent, openSaasModal, closeModal } = useOnboardModal();
    const [slide, setSlide] = useState(0);
    const [demoSlide, setDemoSlide] = useState(0);
    const dragStartX = useRef<number | null>(null);

    const campaignOffer = !isPlatform && activePreset === "beauty" && isCampaignOfferActive(campaign);
    const campaignScheduled =
        !isPlatform && activePreset === "beauty" && campaign?.status === "SCHEDULED";
    const campaignSoldOut = !isPlatform && activePreset === "beauty" && campaign?.status === "SOLD_OUT";

    const platformRotate = t("home.hero.rotate", { returnObjects: true }) as string[];
    const presetRotate = t(`home.hero.rotatePreset.${activePreset}`, { returnObjects: true }) as string[];
    const rotateLines = isPlatform
        ? platformRotate
        : Array.isArray(presetRotate) && presetRotate.length
          ? presetRotate
          : (t("home.hero.rotatePreset.beauty", { returnObjects: true }) as string[]);
    const rotating = useRotatingLine(
        rotateLines,
        (isPlatform || !campaignOffer) && !campaignScheduled,
        reduceMotion,
    );

    const trust = t("home.hero.trust", { returnObjects: true }) as { value: string; label: string }[];
    const demoCopy = t("home.hero.demos", { returnObjects: true }) as {
        phoneCaption: string;
        crmCaption: string;
        label: string;
    }[];
    const demoSlides = PLATFORM_DEMO_ASSETS.map((asset, i) => ({
        ...asset,
        phoneCaption: demoCopy[i]?.phoneCaption ?? "",
        crmCaption: demoCopy[i]?.crmCaption ?? "",
        label: demoCopy[i]?.label ?? "",
    }));

    const slides = preset.slides;

    useEffect(() => {
        setSlide(0);
    }, [activePreset, isPlatform]);

    useEffect(() => {
        if (isPlatform || reduceMotion || slides.length <= 1) return undefined;
        const id = window.setInterval(() => {
            setSlide((current) => (current + 1) % slides.length);
        }, 5200);
        return () => window.clearInterval(id);
    }, [activePreset, isPlatform, slides.length, reduceMotion]);

    useEffect(() => {
        if (!isPlatform || reduceMotion || demoSlides.length <= 1) return undefined;
        const id = window.setInterval(() => {
            setDemoSlide((current) => (current + 1) % demoSlides.length);
        }, 4500);
        return () => window.clearInterval(id);
    }, [isPlatform, reduceMotion, demoSlides.length]);

    const openTrial = () => {
        if (campaignOffer) {
            trackBeautyCampaignEvent("beauty_campaign_cta_click", campaign, { source_ui: "hero" });
            trackBeautyCampaignEvent("beauty_campaign_signup_start", campaign, { source_ui: "hero" });
            openSaasModal("start", "Beauty 100", {
                industryPreset: "BEAUTY",
                leadSource: "beauty_100_2026_hero",
                campaignCode: campaign?.campaign || "BEAUTY_100_2026",
            });
            return;
        }
        window.location.href = startHref(isPlatform ? undefined : activePreset);
    };

    const go = useCallback(
        (dir: -1 | 1) => {
            setSlide((current) => (current + dir + slides.length) % slides.length);
        },
        [slides.length],
    );

    const onPointerDown = (event: ReactPointerEvent) => {
        dragStartX.current = event.clientX;
    };

    const onPointerUp = (event: ReactPointerEvent) => {
        if (dragStartX.current == null) return;
        const delta = event.clientX - dragStartX.current;
        dragStartX.current = null;
        if (Math.abs(delta) < 40) return;
        go(delta < 0 ? 1 : -1);
    };

    const active = slides[slide] ?? slides[0];
    const prev = slides[(slide - 1 + slides.length) % slides.length];
    const next = slides[(slide + 1) % slides.length];
    const demo = demoSlides[demoSlide] ?? demoSlides[0];
    const slideTransition = reduceMotion ? { duration: 0 } : { duration: 0.55, ease: "easeInOut" as const };
    const demoTransition = reduceMotion ? { duration: 0 } : { duration: 0.45, ease: "easeInOut" as const };
    const rotateTransition = reduceMotion ? { duration: 0 } : { duration: 0.32, ease: "easeOut" as const };

    const head = isPlatform
        ? t("home.hero.headTop")
        : t("home.hero.presetHeadTemplate", { label: marketingPreset?.label || preset.shortLabel });
    const sub = isPlatform
        ? t("home.hero.sub")
        : marketingPreset?.subhead || t("home.hero.presetSubFallback");

    const ctaLabel = campaignOffer
        ? t("home.hero.cta.getTezamFree")
        : isPlatform
          ? t("home.hero.cta.createFree")
          : t("home.hero.cta.tryFree");

    return (
        <section
            className={cx("hero", isPlatform && "heroPlatform")}
            data-hero
            style={
                {
                    ["--preset-accent" as string]: isPlatform ? "#229ed9" : preset.accent,
                    ["--preset-accent-soft" as string]: isPlatform
                        ? "rgba(34, 158, 217, 0.18)"
                        : preset.accentSoft,
                } as CSSProperties
            }
        >
            <div className={cx("shell", "container")}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={isPlatform ? "platform" : activePreset}
                        className={cx("intro", isPlatform && "introWide")}
                        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? undefined : { opacity: 0, y: -6 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                        {campaignOffer || campaignScheduled ? (
                            <>
                                <p className={cx("campaignEyebrow")}>{t("home.hero.campaign.eyebrow")}</p>
                                <h1 className={cx("headline")}>
                                    <span className={cx("utpCore")}>{t("home.hero.campaign.head")}</span>
                                    <span className={cx("rotateSlot")}>
                                        <span className={cx("rotateLine")}>{t("home.hero.campaign.headLine")}</span>
                                    </span>
                                </h1>
                                <p className={cx("subtitle")}>
                                    {campaignScheduled
                                        ? t("home.hero.campaign.subScheduled", {
                                              date: formatCampaignDate(campaign?.startsAt) || t("home.hero.campaign.soon"),
                                              max: campaign?.maxActivations ?? 100,
                                              months: campaign?.freeMonths ?? 12,
                                          })
                                        : t("home.hero.campaign.subActive", {
                                              max: campaign?.maxActivations ?? 100,
                                              months: campaign?.freeMonths ?? 12,
                                          })}
                                </p>
                            </>
                        ) : isPlatform ? (
                            <>
                                <p className={cx("eyebrow")}>{t("home.hero.eyebrow")}</p>
                                <h1 className={cx("headline", "headlineStable", "headlineHero")}>
                                    <span className={cx("utpCore")}>{t("home.hero.headTop")}</span>
                                    <span className={cx("utpCore", "utpCoreWide")}>{t("home.hero.headBottom")}</span>
                                    <span className={cx("rotateSlot")} aria-live="polite">
                                        <AnimatePresence mode="wait" initial={false}>
                                            <motion.span
                                                key={rotating}
                                                className={cx("rotateLine")}
                                                initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={reduceMotion ? undefined : { opacity: 0, y: -12 }}
                                                transition={rotateTransition}
                                            >
                                                {rotating}
                                            </motion.span>
                                        </AnimatePresence>
                                    </span>
                                </h1>
                                <p className={cx("subtitle", "subtitleWide")}>{sub}</p>
                            </>
                        ) : (
                            <>
                                <h1 className={cx("headline")}>
                                    <span className={cx("utpCore")}>{head}</span>
                                    <span className={cx("rotateSlot")} aria-live="polite">
                                        <AnimatePresence mode="wait" initial={false}>
                                            <motion.span
                                                key={rotating}
                                                className={cx("rotateLine")}
                                                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={reduceMotion ? undefined : { opacity: 0, y: -10 }}
                                                transition={rotateTransition}
                                            >
                                                {rotating}
                                            </motion.span>
                                        </AnimatePresence>
                                    </span>
                                </h1>
                                <p className={cx("subtitle")}>{sub}</p>
                                {campaignSoldOut ? (
                                    <p className={cx("soldOut")}>{t("home.hero.campaign.soldOut")}</p>
                                ) : null}
                            </>
                        )}

                        <div className={cx("ctaRow")}>
                            <button
                                type="button"
                                className={cx("cta", "ctaPrimary", campaignOffer && "ctaBeauty")}
                                onClick={openTrial}
                            >
                                {ctaLabel}
                            </button>
                            {!campaignOffer && isPlatform ? (
                                <a className={cx("secondaryCta")} href="#how-it-works">
                                    {t("home.hero.cta.watchDemo")}
                                </a>
                            ) : null}
                            {!campaignOffer && !isPlatform ? (
                                <a className={cx("secondaryCta")} href={`/solutions/${activePreset}`}>
                                    {t("home.hero.cta.moreAboutIndustry")}
                                </a>
                            ) : null}
                            {campaignOffer ? (
                                <p className={cx("ctaRemain")}>
                                    {campaignLoading
                                        ? t("home.hero.cta.checkingCampaign")
                                        : remainingPlacesOfMax(
                                              campaign?.remaining ?? 0,
                                              campaign?.maxActivations ?? 100,
                                          )}
                                </p>
                            ) : null}
                        </div>
                        {!campaignOffer ? (
                            <p className={cx("ctaNote")}>
                                {isPlatform ? t("home.hero.ctaNotePlatform") : t("home.hero.ctaNotePreset")}
                            </p>
                        ) : null}
                    </motion.div>
                </AnimatePresence>

                {isPlatform ? (
                    <div className={cx("proofStack")} aria-label={t("home.hero.proofSliderAriaLabel")}>
                        <div className={cx("trustFloat", "trustTL")}>
                            <strong>{trust[0]?.value}</strong>
                            <span>{trust[0]?.label}</span>
                        </div>
                        <div className={cx("trustFloat", "trustTR")}>
                            <strong>{trust[1]?.value}</strong>
                            <span>{trust[1]?.label}</span>
                        </div>
                        <div className={cx("trustFloat", "trustBL")}>
                            <strong>{trust[2]?.value}</strong>
                            <span>{trust[2]?.label}</span>
                        </div>
                        <div className={cx("trustFloat", "trustBR")}>
                            <strong>{trust[3]?.value}</strong>
                            <span>{trust[3]?.label}</span>
                        </div>

                        <div className={cx("stackStage")}>
                            <div className={cx("stackCrm")} aria-hidden="true">
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.img
                                        key={demo.crm}
                                        src={demo.crm}
                                        alt=""
                                        width={720}
                                        height={450}
                                        decoding="async"
                                        initial={reduceMotion ? false : { opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={reduceMotion ? undefined : { opacity: 0 }}
                                        transition={demoTransition}
                                    />
                                </AnimatePresence>
                            </div>
                            <div className={cx("stackPhone")}>
                                <div className={cx("phone", "phoneMain")}>
                                    <div className={cx("phoneScreen")}>
                                        <AnimatePresence mode="wait" initial={false}>
                                            <motion.img
                                                key={demo.phone}
                                                className={cx("phoneImage")}
                                                src={demo.phone}
                                                alt={`${demo.phoneCaption}. ${demo.crmCaption}`}
                                                width={400}
                                                height={860}
                                                decoding="async"
                                                draggable={false}
                                                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                                                transition={demoTransition}
                                            />
                                        </AnimatePresence>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className={cx("stackCapRow")}>
                            <span>{demo.phoneCaption}</span>
                            <span className={cx("stackCapSep")} aria-hidden="true">
                                ·
                            </span>
                            <span>{demo.crmCaption}</span>
                        </p>
                    </div>
                ) : (
                    <div
                        className={cx("stage")}
                        onPointerDown={onPointerDown}
                        onPointerUp={onPointerUp}
                        onPointerCancel={() => {
                            dragStartX.current = null;
                        }}
                        role="region"
                        aria-roledescription="carousel"
                        aria-label={t("home.hero.miniAppScreensAriaLabel", { label: preset.shortLabel })}
                    >
                        <div className={cx("fan")}>
                            <div className={cx("phone", "phoneSide", "phoneLeft")} aria-hidden="true">
                                <div className={cx("phoneScreen")}>
                                    <img className={cx("phoneImage")} src={prev.image} alt="" />
                                </div>
                            </div>
                            <div className={cx("phone", "phoneMain")}>
                                <div className={cx("phoneScreen")}>
                                    <AnimatePresence mode="wait" initial={false}>
                                        <motion.img
                                            key={active.image}
                                            className={cx("phoneImage")}
                                            src={active.image}
                                            alt={active.caption}
                                            initial={reduceMotion ? false : { opacity: 0, x: 18 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={reduceMotion ? undefined : { opacity: 0, x: -18 }}
                                            transition={slideTransition}
                                            draggable={false}
                                        />
                                    </AnimatePresence>
                                </div>
                            </div>
                            <div className={cx("phone", "phoneSide", "phoneRight")} aria-hidden="true">
                                <div className={cx("phoneScreen")}>
                                    <img className={cx("phoneImage")} src={next.image} alt="" />
                                </div>
                            </div>
                        </div>
                        <p className={cx("caption")}>{active.caption}</p>
                    </div>
                )}

                {isPlatform ? <p className={cx("trustBar")}>{t("home.hero.trustBar")}</p> : null}
            </div>

            <ModalCallBack isOpen={isModalOpen} onClose={closeModal} leadIntent={leadIntent} />
        </section>
    );
};
